import React, { useMemo } from 'react';
import { st, classes } from './Text.st.css';
import { WixStyleReactMaskingContext } from '../WixStyleReactMaskingProvider/context';
import { TextProps } from './Text.types';
import { RenderProps } from '../common/Ellipsis';

type StyleAttributes = Record<string, unknown>;

const getStyleDataAttributes = (
  styleAttributes: StyleAttributes,
): StyleAttributes =>
  Object.keys(styleAttributes).reduce((acc: StyleAttributes, styleKey) => {
    acc[`data-${styleKey}`] = styleAttributes[styleKey];
    return acc;
  }, {});

export type RawTextProps = TextProps &
  Required<Pick<RenderProps, 'renderChildren' | 'renderElement'>>;
type DefaultProps = Required<
  Pick<
    RawTextProps,
    | 'size'
    | 'secondary'
    | 'skin'
    | 'light'
    | 'weight'
    | 'tagName'
    | 'listStyle'
    | 'widows'
    | 'overflowWrap'
  >
>;
type Props = RawTextProps & DefaultProps;

export const RawTextDefaultProps: DefaultProps = {
  size: 'medium',
  secondary: false,
  skin: 'standard',
  light: false,
  weight: 'thin',
  tagName: 'span',
  listStyle: 'checkmark',
  widows: false,
  overflowWrap: 'normal',
};

const RawText = React.forwardRef<HTMLElement, Props>(
  (
    {
      size = RawTextDefaultProps.size,
      secondary = RawTextDefaultProps.secondary,
      skin = RawTextDefaultProps.skin,
      light = RawTextDefaultProps.light,
      weight = RawTextDefaultProps.weight,
      tagName = RawTextDefaultProps.tagName,
      children,
      ellipsis,
      appendTo,
      flip,
      fixed,
      placement,
      maxWidth,
      zIndex,
      showTooltip,
      listStyle = RawTextDefaultProps.listStyle,
      id,
      widows = RawTextDefaultProps.widows,
      suffix,
      overflowWrap = RawTextDefaultProps.overflowWrap,
      renderChildren,
      renderElement,
      ...rest
    },
    ref,
  ) => {
    const { dataHook, className, ...textProps } = rest;

    const styleAttributes = {
      size,
      secondary,
      skin,
      light,
      weight,
      widows,
      'list-style': listStyle,
      'overflow-wrap': overflowWrap,
    };
    const styleDataAttributes = getStyleDataAttributes(styleAttributes);

    const childrenWidows = useMemo(() => {
      if (!widows || typeof children !== 'string' || !children) {
        return children;
      }
      const textArray = children.split(' ');
      if (textArray.length <= 2) return children;
      const arrLength = textArray.length;
      const newChildrenArr = textArray.slice(0, arrLength - 2);
      newChildrenArr.push(
        textArray[arrLength - 2] + '\u00A0' + textArray[arrLength - 1],
      );
      return newChildrenArr.join(' ');
    }, [children, widows]);

    return (
      <WixStyleReactMaskingContext.Consumer>
        {({ maskingClassNames }) => {
          const element = React.createElement(
            tagName,
            {
              ref,
              id,
              ...textProps,
              'data-hook': dataHook,
              'data-mask': !!maskingClassNames,
              className: st(
                classes.root,
                styleAttributes,
                className,
                maskingClassNames,
              ),
              ...styleDataAttributes,
            },
            renderChildren({ text: childrenWidows, suffix }),
          );

          return renderElement({
            element,
            suffix,
          });
        }}
      </WixStyleReactMaskingContext.Consumer>
    );
  },
);

RawText.displayName = 'Text';

export { RawText };
